import { useEffect, useMemo } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router';
import { Save } from 'lucide-react';
import * as Slider from '@radix-ui/react-slider';
import { toast } from 'sonner';

import {
  type LLMsViewType,
  SelectedLLM,
  updateLLMSchema,
  type UpdateLLMInput
} from '@intone/shared';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  useUpdateLLM,
  useLLM,
  getValidStatesConfig,
  getProviderForModel,
  MODEL_OPTIONS,
  VIEW_TYPE_LABELS_MAP,
  DEFAULT_EVAL_FORMAT,
} from '@/entities/llm';
import { useToolsList } from '@/entities/tool';
import { useUserData } from '@/entities/user';

import { TemplateBlock } from './TemplateBlock';
import { AgentStateBlock } from './AgentStateBlock';
import { useUnsavedChanges } from '../lib/FormStatusProvider';

function mapServerData(llm: SelectedLLM, viewType: LLMsViewType, isSuperadmin: boolean): UpdateLLMInput {
  const testConf = isSuperadmin ? {
    evaluation_output_format: llm.test_configuration?.evaluation_output_format ?? DEFAULT_EVAL_FORMAT
  } : llm.test_configuration;

  return {
    ...llm,
    states_config: getValidStatesConfig(llm.states_config, viewType),
    test_configuration: testConf,
  };
}

type LLMEditorFormProps = {
  llm: SelectedLLM;
  isFetching: boolean;
}

export function LLMEditorForm({llm, isFetching}: LLMEditorFormProps) {
  const { isSuperadmin } = useUserData();

  const { data: tools } = useToolsList();

  const updateLLM = useUpdateLLM();

  const viewType: LLMsViewType = (llm.view_type as LLMsViewType) ?? 'single_prompt';
  const showSinglePrompt = viewType === 'single_prompt';
  const showTemplate = viewType === 'template';
  // For template view with hasPromptEditor, show states info as well
  const showTemplateStates = showTemplate && isSuperadmin;

  const initialFormValues = useMemo(
    () => mapServerData(llm, viewType, isSuperadmin),
    [llm, viewType, isSuperadmin]
  );

  const methods = useForm<UpdateLLMInput>({
    resolver: zodResolver(updateLLMSchema),
    values: initialFormValues,
    resetOptions: {
      keepDefaultValues: false,
      keepDirtyValues: true,
    },
  });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { isDirty, errors },
  } = methods;

  const { setIsDirty } = useUnsavedChanges();

  useEffect(() => {
    setIsDirty(isDirty);
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  const template = watch('template');
  const statesConfig = watch('states_config');

  const sortedStates = useMemo(() => {
    if (!statesConfig) return [];
    const entries = Object.entries(statesConfig);
    const initial = entries.find(([, s]) => s.transition);
    const others = entries.filter(([, s]) => !s.transition);
    return initial ? [initial, ...others] : others;
  }, [statesConfig]);

  const allStateNames = useMemo(() => Object.keys(statesConfig), [statesConfig]);

  const onSubmit = (data: UpdateLLMInput) => {
    if (!llm.id) return;

    updateLLM.mutate(
      {
        ...data,
        id: llm.id,
        provider: getProviderForModel(data.model),
      },
      {
        onSuccess: ({message}) => {
          toast.success(message, {id: 'llm-update-success' });
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to save LLM', {
            id: 'llm-update-error',
          }),
      }
    );
  };

  const isSaving = updateLLM.isPending;

  return (
    <div className="relative">
      {isFetching && (
        <div className='absolute inset-0 flex justify-center z-10 pt-[17%] bg-white/50'>
          <span className="text-body text-gray-500 animate-pulse">Loading LLM details...</span>
        </div>
      )}
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`px-4 space-y-3 transition-opacity duration-200 ${isFetching ? 'opacity-20' : 'opacity-100'}`}
        >
          {/* Settings Card */}
          <Card className="border-gray-300">
            <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-body font-semibold">LLM Settings</CardTitle>

                <div className="flex items-center gap-2">
                  {isDirty && (
                    <span className="text-detail text-destructive">You have unsaved changes</span>
                  )}

                  <Button type="submit" size="sm" disabled={isSaving || !isDirty}>
                    <Save className="w-3 h-3 mr-2" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* Name */}
              <div className="grid gap-1">
                <Label htmlFor="llm-name" className="font-medium text-gray-700">Name</Label>
                <Input
                  id="llm-name"
                  {...register('name')}
                  placeholder="Enter LLM name"
                  className="h-8 max-w-sm"
                />
                {errors.name ? (
                  <span className="text-detail text-destructive">{errors.name.message}</span>
                ) : (
                  <span className="text-detail text-gray-500">
                  {VIEW_TYPE_LABELS_MAP[viewType] || viewType}
                </span>
                )}
              </div>

              <div className="flex items-start gap-4">
                {/* Model Selection */}
                <div className="w-1/2 grid gap-1">
                  <Label htmlFor="model" className="font-medium text-gray-700">Model</Label>
                  <Controller
                    name="model"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <Select value={value} onValueChange={onChange}>
                        <SelectTrigger className="h-8 max-w-xs" ref={ref} id="model">
                          <SelectValue placeholder="Select model" />
                        </SelectTrigger>
                        <SelectContent>
                          {MODEL_OPTIONS.map((option) => (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                              disabled={!option.enabled}
                              className={!option.enabled ? 'opacity-50' : ''}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.model && (<span className="text-detail text-destructive">{errors.model.message}</span>)}
                </div>

                {/* Temperature */}
                <div className="w-1/2 grid gap-1">
                  <Label className="font-medium text-gray-700">Temperature</Label>
                  <Controller
                    name="model_temperature"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <Slider.Root
                          value={[field.value]}
                          min={0}
                          max={1}
                          step={0.01}
                          onValueChange={([val]) => field.onChange(val)}
                          className="grow relative flex h-8 touch-none select-none items-center"
                        >
                          <Slider.Track className="relative h-1 grow rounded-full bg-gray-200">
                            <Slider.Range className="absolute h-full rounded-full bg-gray-900" />
                          </Slider.Track>
                          <Slider.Thumb
                            className="block size-4 rounded-full bg-white border border-gray-400 shadow-sm"
                            aria-label="Temperature"
                          />
                        </Slider.Root>
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.01}
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          className="h-8 w-10 p-1 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                        />
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Start Phrase */}
              <div className="grid gap-1">
                <Label htmlFor="start_phrase" className="font-medium text-gray-700">Start Phrase</Label>
                <Input
                  id="start_phrase"
                  {...register('start_phrase')}
                  placeholder="Enter the start phrase for the agent"
                  className="h-8"
                />
                {errors.start_phrase && (<span className="text-detail text-destructive">{errors.start_phrase.message}</span>)}
              </div>
            </CardContent>
          </Card>

          {/* Template Block - only for template view */}
          {showTemplate && (
            <TemplateBlock
              template={template || {}}
              onUpdate={(next) => setValue('template', next, { shouldDirty: true })}
            />
          )}

          {/* States Configuration - show for template+hasPromptEditor OR non-template views */}
          {(showTemplateStates || !showTemplate) && (
            <div className="space-y-3">
              {/* Section header for template states */}
              {showTemplateStates && (
                <div className="border-t border-gray-300 pt-4">
                  <h3 className="text-heading font-semibold">States Configuration</h3>
                  <p className="text-detail text-gray-500 mt-1">
                    Configure states and their tools. Template prompts are injected into states
                    during calls.
                  </p>
                </div>
              )}

              {/* Render fixed states using AgentStateBlock */}
              {/* Output initial state first, then remaining states */}
              {sortedStates.map(([stateName, stateConfig]) => (
                <AgentStateBlock
                  key={stateName}
                  blockType={showSinglePrompt ? 'single_prompt' : (stateConfig.transition ? 'double_start' : 'double_end')}
                  stateName={stateName}
                  otherStateNames={allStateNames.filter(n => n !== stateName)}
                  tools={tools ?? []}
                />
              ))}
            </div>
          )}

          {/* Test Configuration - superadmin only */}
          {isSuperadmin && (
            <Card className="border-gray-300">
              <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
                <CardTitle className="text-body font-semibold">Test Configuration</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="grid gap-1">
                  <Label className="font-medium text-gray-700">Evaluation Output Format</Label>
                  <Textarea
                    {...register('test_configuration.evaluation_output_format')}
                    placeholder="Instructions appended to all evaluation prompts..."
                    className="min-h-[120px] resize-y font-mono text-sm"
                  />
                  <p className="text-detail text-gray-400">
                    This instruction will be automatically appended to all evaluation prompts
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </form>
      </FormProvider>
    </div>
  );
}

export function ScriptEditor() {
  const { id } = useParams<{ id?: string }>();
  const selectedLLMId = id ? Number(id) : null;

  const { data: llm, isLoading, isSuccess, isError, error, isFetching } = useLLM(selectedLLMId);

  if (!isSuccess || !selectedLLMId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        {!selectedLLMId && <p className="text-body">Select an LLM to start editing</p>}
        {isLoading && <p className="text-body animate-pulse">Loading LLM details...</p>}
        {isError && <p className="text-body text-destructive">Error: {error.message}</p>}
      </div>
    );
  }

  return (<LLMEditorForm key={id} llm={llm} isFetching={isFetching} />);
}